import { z } from "zod";

/** Optional budget tier the plan is tailored to. */
export const aestheticBudgetTierSchema = z.enum([
	"economy",
	"standard",
	"premium",
]);
export type AestheticBudgetTier = z.infer<typeof aestheticBudgetTierSchema>;

/** A single recommended treatment line inside a phase. */
export const aestheticPlanItemSchema = z.object({
	treatment: z.string().max(200),
	area: z.string().max(120).optional(),
	sessions: z.number().int().positive().max(60).optional(),
	interval: z.string().max(120).optional(),
	rationale: z.string().max(400).optional(),
});
export type AestheticPlanItem = z.infer<typeof aestheticPlanItemSchema>;

/** A phase of the plan (sequenced over time). */
export const aestheticPlanPhaseSchema = z.object({
	title: z.string().max(160),
	timeframe: z.string().max(120).optional(),
	items: z.array(aestheticPlanItemSchema).max(12),
});
export type AestheticPlanPhase = z.infer<typeof aestheticPlanPhaseSchema>;

/** The structured payload the model must return. */
export const aestheticPlanResultSchema = z.object({
	summary: z.string().max(2000),
	objectives: z.array(z.string().max(200)).max(8),
	phases: z.array(aestheticPlanPhaseSchema).max(6),
	precautions: z.array(z.string().max(300)).max(10),
	caveat: z.string().max(600).optional(),
});
export type AestheticPlanResult = z.infer<typeof aestheticPlanResultSchema>;

export const aestheticPlanApiSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	visit_id: z.string().uuid().nullable(),
	goals: z.string(),
	budget_tier: aestheticBudgetTierSchema.nullable(),
	summary: z.string().nullable(),
	objectives: z.array(z.string()),
	phases: z.array(aestheticPlanPhaseSchema),
	precautions: z.array(z.string()),
	caveat: z.string().nullable(),
	model_version: z.string().nullable(),
	created_by_member_id: z.string().uuid().nullable(),
	created_by_name: z.string().nullable(),
	created_at: z.string().datetime(),
});
export type AestheticPlanApi = z.infer<typeof aestheticPlanApiSchema>;

/** Generate a plan from the patient's aesthetic picture + the clinician's goals. */
export const generateAestheticPlanInputSchema = z.object({
	patient_id: z.string().uuid(),
	visit_id: z.string().uuid().optional(),
	goals: z.string().min(3).max(2000),
	budget_tier: aestheticBudgetTierSchema.optional(),
});
export type GenerateAestheticPlanInput = z.infer<
	typeof generateAestheticPlanInputSchema
>;
